import { Button } from '@/components/ui/button';
import { 
  MapPin, 
  Users, 
  Heart, 
  BarChart3,
  CheckCircle,
  ArrowRight
} from 'lucide-react';

const BOOKING_URL = "https://link.sweatsetter.com/widget/booking/0bvqp14e0y6pNvAdcqsM";
const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/0q7uoo2s_tire-raise-wide.jpg";

const differentiators = [
  { icon: MapPin, title: 'Indoor and Outdoor Training', desc: 'Train in Scott\'s Addition and at outdoor locations around Richmond.' },
  { icon: Users, title: 'Multiple Coaching Formats', desc: 'Choose from personal training, semi-private, large group, and HYROX training.' },
  { icon: Heart, title: 'Support Beyond Workouts', desc: 'Nutrition coaching and progress tracking help connect effort to results.' },
  { icon: BarChart3, title: 'Community and Accountability', desc: 'Members support each other, coaches know your name, and consistency becomes easier.' },
];

const whoWeHelp = [
  'beginners getting back into fitness',
  'busy professionals',
  'adults who want to lose weight and build strength',
  'people who need accountability',
  'athletes and competitors training for events like HYROX'
];

const beliefs = [
  'you are coached',
  'your workouts stay varied',
  'your progress is measured',
  'your community helps you stay accountable',
  'your program fits real life'
];

export default function About() {
  return (
    <div data-testid="about-page">
      {/* Hero Section - Full Width Image */}
      <section 
        className="relative flex items-center justify-center"
        style={{ height: '735px', marginTop: '0' }}
        data-testid="about-hero"
        aria-label="About hero"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${HERO_IMAGE})`,
            top: '135px',
            height: '600px'
          }}
        >
        </div>
        
        {/* Hero Content */}
        <div className="relative z-10 container-custom text-center" style={{ marginTop: '135px' }}>
          <div className="max-w-3xl mx-auto">
            {/* Text overlay box with semi-transparent background */}
            <div className="bg-black/30 p-[50px] inline-block">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading text-white mb-6">
                More Than a Gym. A Coaching-Based Fitness Community.
              </h1>
              <p className="text-lg text-white/90 mb-4">
                X-Team was built for people who want more than access to equipment.
              </p>
              <p className="text-lg text-white/90 mb-8">
                We help adults in Richmond get stronger, healthier, and more consistent through expert coaching, accountability, variety, measurable progress, and community.
              </p>
              <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
                <Button 
                  data-testid="about-hero-cta"
                  className="bg-[#FF5A3C] hover:bg-[#E64526] text-white rounded-none font-nav uppercase tracking-widest px-8 py-4 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                >
                  Book a Free Intro
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Why We Exist */}
      <section className="pt-8 pb-16 bg-white" data-testid="why-we-exist">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-6 text-center">
              Why X-Team Exists
            </h2>
            <p className="text-lg text-gray-700 mb-6 text-center">
              Too many people join gyms and never get the coaching, structure, or accountability they need to succeed.
            </p>
            <p className="text-lg text-gray-700 mb-8 text-center">
              They start strong, lose momentum, and end up back where they began.
            </p>
            <p className="text-xl text-[#1A1A1A] font-semibold mb-8 text-center">
              X-Team was created to change that.
            </p>
            
            <div className="bg-[#F5F7FA] rounded-none p-8">
              <p className="text-lg text-gray-700 mb-6 text-center">
                We believe fitness works better when:
              </p>
              <ul className="space-y-4 max-w-md mx-auto">
                {beliefs.map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-[#FF5A3C] flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* What Makes X-Team Different */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="what-makes-different" aria-label="What makes X-Team different">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-4">
              A Better Way to Train in Richmond
            </h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-8">
            {differentiators.map((item, i) => (
              <div 
                key={i} 
                className="bg-white rounded-none p-8 shadow-[0_4px_20px_rgba(0,0,0,0.05)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.08)] transition-all duration-300"
                data-testid={`differentiator-${i}`}
              >
                <div className="w-14 h-14 rounded-none bg-gray-100 flex items-center justify-center mb-6">
                  <item.icon className="w-7 h-7 text-[#FF5A3C]" />
                </div>
                <h3 className="font-heading text-xl text-[#1A1A1A] mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Help */}
      <section className="section-padding bg-white" data-testid="who-we-help" aria-label="Who we help">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="relative">
              <img 
                src="https://customer-assets.emergentagent.com/job_3bc90ea7-8a21-4aef-876a-f2085af8729d/artifacts/f37u9htn_tugofwar.jpg" 
                alt="X-Team Members playing tug of war"
                className="rounded-none shadow-xl w-full"
                loading="lazy"
              />
            </div>
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-6">
                Who We Help
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We work with:
              </p>
              <ul className="space-y-4 mb-8">
                {whoWeHelp.map((item, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-[#FF5A3C] flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-lg text-gray-700 font-semibold">
                No matter where you are starting, we will meet you there.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team / Culture */}
      <section className="section-padding bg-[#F5F7FA]" data-testid="team-culture" aria-label="Team and culture">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading text-[#1A1A1A] mb-6">
              Coaches Who Know Your Name — and Care Where You're Going
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              At X-Team, you are not just another membership number. Our coaches are here to guide you, challenge you, and help you stay consistent over time.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="bg-white rounded-none p-6 shadow-sm">
                <div className="w-20 h-20 rounded-none bg-gray-100 mx-auto mb-4 flex items-center justify-center">
                  <Users className="w-10 h-10 text-[#FF5A3C]" />
                </div>
                <h3 className="font-heading text-lg text-[#1A1A1A]">Expert Coaches</h3>
                <p className="text-gray-600 text-sm mt-2">Certified professionals dedicated to your success</p>
              </div>
              <div className="bg-white rounded-none p-6 shadow-sm">
                <div className="w-20 h-20 rounded-none bg-gray-100 mx-auto mb-4 flex items-center justify-center">
                  <Heart className="w-10 h-10 text-[#FF5A3C]" />
                </div>
                <h3 className="font-heading text-lg text-[#1A1A1A]">Supportive Community</h3>
                <p className="text-gray-600 text-sm mt-2">Members who lift each other up</p>
              </div>
              <div className="bg-white rounded-none p-6 shadow-sm">
                <div className="w-20 h-20 rounded-none bg-gray-100 mx-auto mb-4 flex items-center justify-center">
                  <BarChart3 className="w-10 h-10 text-[#FF5A3C]" />
                </div>
                <h3 className="font-heading text-lg text-[#1A1A1A]">Results-Focused</h3>
                <p className="text-gray-600 text-sm mt-2">Measurable progress you can see and feel</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-gradient-to-br from-[#FF5A3C] to-[#E64526] text-white" data-testid="about-final-cta" aria-label="Call to action">
        <div className="container-custom text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading mb-6">
            Come See if X-Team is Right for You
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Book a Free Intro, talk with a coach, and take the first step toward feeling better, moving better, and getting stronger.
          </p>
          <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer">
            <Button 
              data-testid="about-final-cta-btn"
              className="bg-white text-[#FF5A3C] hover:bg-gray-100 rounded-none font-nav uppercase tracking-widest px-10 py-6 text-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1"
            >
              Book a Free Intro <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
}
